import { useTranslation } from 'react-i18next';

import {
  GRADIENT_ANGLES,
  GRADIENT_PRESETS,
  type Gradient,
  gradientCss,
} from '@/entities/appearance';
import { ColorSwatches } from '@/shared/ui';

import styles from './editor.module.css';

const ARROWS: Record<number, string> = {
  0: '↑',
  45: '↗',
  90: '→',
  135: '↘',
  180: '↓',
  225: '↙',
  270: '←',
  315: '↖',
};

interface Props {
  value: Gradient;
  onChange: (value: Gradient) => void;
}

/** Two colors and a direction: ready-made presets, custom colors, 8 directions + a slider. */
export function GradientEditor({ value, onChange }: Props) {
  const { t } = useTranslation();
  return (
    <div className={styles.section}>
      <p className={styles.sectionTitle}>{t('appearance.gradient.presets')}</p>
      <div className={styles.presets}>
        {GRADIENT_PRESETS.map((preset, index) => (
          <button
            key={`${preset.start}-${preset.end}`}
            type="button"
            className={styles.preset}
            aria-label={t('appearance.gradient.preset', { number: index + 1 })}
            aria-pressed={preset.start === value.start && preset.end === value.end}
            style={{ background: gradientCss({ ...preset, angle: 90 }) ?? undefined }}
            onClick={() => {
              onChange({ ...preset, angle: value.angle });
            }}
          />
        ))}
      </div>
      <ColorSwatches
        label={t('appearance.gradient.start')}
        value={value.start}
        onChange={(start) => {
          if (start) onChange({ ...value, start });
        }}
      />
      <ColorSwatches
        label={t('appearance.gradient.end')}
        value={value.end}
        onChange={(end) => {
          if (end) onChange({ ...value, end });
        }}
      />
      <p className={styles.sectionTitle}>{t('appearance.gradient.direction')}</p>
      <div className={styles.angles} role="group" aria-label={t('appearance.gradient.direction')}>
        {GRADIENT_ANGLES.map((angle) => (
          <button
            key={angle}
            type="button"
            className={styles.angle}
            aria-pressed={value.angle === angle}
            aria-label={t('appearance.gradient.angle', { angle })}
            onClick={() => {
              onChange({ ...value, angle });
            }}
          >
            {ARROWS[angle]}
          </button>
        ))}
      </div>
      <label className={styles.slider}>
        <span>{t('appearance.gradient.angle', { angle: value.angle })}</span>
        <input
          type="range"
          min={0}
          max={360}
          step={5}
          value={value.angle}
          onChange={(event) => {
            onChange({ ...value, angle: Number(event.target.value) });
          }}
        />
      </label>
    </div>
  );
}
